import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm, useWatch, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { HtmlEditor } from '@/components/HtmlEditor';
import '@/styles/admin-emails.css';
import { LocationCombobox, type LocationOption } from '@/components/LocationCombobox';
import { Select, type SelectOption } from '@/components/Select/Select';
import { TextField } from '@/components/TextField';
import { Textarea } from '@/components/Textarea';
import { useAuth } from '@/contexts/AuthContext';
import { useFormDirty } from '@/hooks/useFormDirty';
import { useFormDraftAutosave } from '@/hooks/useFormDraftAutosave';
import { createAdminDeal, type AdminDealCreatePayload } from '@/lib/db/admin/deals';
import { searchDldBuildings, searchDldCommunities, searchDldZones } from '@/lib/db/dld-locations';
import { adminDealCreateSchema, type AdminDealCreateValues } from '@/lib/admin-deal-schemas';
import { PROPERTY_TYPES, type PropertyType } from '@/lib/db/deals';

const TABS = ['type', 'property', 'price', 'media'] as const;
type TabKey = (typeof TABS)[number];

// Ordered mapping field → tab. Drives the auto-switch + focus behaviour when
// `form.handleSubmit` rejects with validation errors on inactive tabs (Spec
// UB14 — « jumplink + scroll + focus si erreur »). Order matches the schema
// composition (`adminDealCreateSchema`) so the first error we find sits on
// the first tab a user would look at.
const FIELD_TAB_ORDER: ReadonlyArray<{ field: keyof AdminDealCreateValues; tab: TabKey }> = [
  { field: 'type', tab: 'type' },
  { field: 'is_featured', tab: 'type' },
  { field: 'expiration_date', tab: 'type' },
  { field: 'internal_notes', tab: 'type' },
  { field: 'title', tab: 'property' },
  { field: 'description', tab: 'property' },
  { field: 'zone', tab: 'property' },
  { field: 'community', tab: 'property' },
  { field: 'building', tab: 'property' },
  { field: 'latitude', tab: 'property' },
  { field: 'longitude', tab: 'property' },
  { field: 'property_type', tab: 'property' },
  { field: 'area_sqft', tab: 'property' },
  { field: 'bedrooms', tab: 'property' },
  { field: 'bathrooms', tab: 'property' },
  { field: 'floor', tab: 'property' },
  { field: 'view', tab: 'property' },
  { field: 'handover', tab: 'property' },
  { field: 'asking_price', tab: 'price' },
  { field: 'original_price', tab: 'price' },
  { field: 'amount_paid', tab: 'price' },
];

function firstErrorLocation(
  errors: FieldErrors<AdminDealCreateValues>,
): { tab: TabKey; field: keyof AdminDealCreateValues } | null {
  for (const { field, tab } of FIELD_TAB_ORDER) {
    if (errors[field]) return { tab, field };
  }
  return null;
}

const NEW_DEFAULTS: AdminDealCreateValues = {
  type: 'ready',
  is_featured: false,
  expiration_date: null,
  advisor_id: null,
  internal_notes: null,
  title: '',
  description: '',
  zone: '',
  community: '',
  building: null,
  zone_key: null,
  community_key: null,
  building_key: null,
  latitude: null,
  longitude: null,
  property_type: 'apartment',
  area_sqft: 0,
  bedrooms: 0,
  bathrooms: null,
  floor: null,
  view: null,
  handover: null,
  asking_price: 0,
  original_price: null,
  amount_paid: null,
};

function getPgError(err: unknown): { code?: string; message: string } {
  if (typeof err === 'object' && err !== null) {
    const candidate = err as { code?: unknown; message?: unknown };
    return {
      code: typeof candidate.code === 'string' ? candidate.code : undefined,
      message: typeof candidate.message === 'string' ? candidate.message : 'Unknown error',
    };
  }
  return { message: String(err) };
}

function AdvisorSelectNew({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const [options, setOptions] = useState<Array<{ id: string; label: string }>>([]);
  useEffect(() => {
    import('@/lib/db/admin/advisors')
      .then(({ listAdvisors }) => listAdvisors())
      .then((advisors) =>
        setOptions(
          advisors
            .filter((a) => a.is_active)
            .map((a) => ({ id: a.id, label: `${a.first_name} ${a.last_name} — ${a.title}` })),
        ),
      )
      .catch(() => {});
  }, []);
  return (
    <div>
      <label
        htmlFor="advisor-new"
        style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem' }}
      >
        Assigned advisor
      </label>
      <select
        id="advisor-new"
        className="m5-field__input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        style={{ maxWidth: '350px' }}
      >
        <option value="">— None —</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function AdminDealNewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const userId = auth.status === 'authenticated' ? auth.profile.id : null;

  const [tab, setTab] = useState<TabKey>('type');
  const [submitting, setSubmitting] = useState(false);

  // Memoise the Zod schema + resolver. Without this, every parent re-render
  // (every keystroke on a registered input, every tab switch) builds a fresh
  // composite Zod object and a fresh resolver, which can show up as observable
  // render churn / input lag on heavier tabs. `t` is identity-stable across
  // renders so `[t]` is the only dep we need.
  const schema = useMemo(() => adminDealCreateSchema(t), [t]);
  const resolver = useMemo(() => zodResolver(schema), [schema]);

  const form = useForm<AdminDealCreateValues>({
    resolver,
    defaultValues: NEW_DEFAULTS,
    mode: 'onSubmit',
  });

  // Track zone + community values so the building / community comboboxes
  // can narrow their RPC search context. useWatch re-renders only the
  // subscribers so the higher-level fields don't re-render the rest of the
  // page on every keystroke.
  // ADR-055 Lot 3 — cascade filters use the SELECTED canonical keys, never a
  // lowercased label guess; NULL key (free text) simply removes the filter.
  const zoneKeyValue = useWatch({ control: form.control, name: 'zone_key' });
  const communityKeyValue = useWatch({ control: form.control, name: 'community_key' });

  const searchZoneOptions = useCallback(async (q: string): Promise<LocationOption[]> => {
    const rows = await searchDldZones(q);
    return rows.map((r) => ({
      key: r.zone_key,
      displayName: r.display_name,
      count: r.transaction_count,
    }));
  }, []);

  const searchCommunityOptions = useCallback(
    async (q: string): Promise<LocationOption[]> => {
      const rows = await searchDldCommunities(q, zoneKeyValue ?? null);
      return rows.map((r) => ({
        key: r.community_key,
        displayName: r.display_name,
        secondary: r.zone ?? undefined,
        count: r.transaction_count,
        zoneKey: r.zone_key ?? undefined,
        zoneDisplayName: r.zone ?? undefined,
      }));
    },
    [zoneKeyValue],
  );

  const searchBuildingOptions = useCallback(
    async (q: string): Promise<LocationOption[]> => {
      const rows = await searchDldBuildings(q, communityKeyValue ?? null, zoneKeyValue ?? null);
      return rows.map((r) => ({
        key: r.building_key,
        displayName: r.display_name,
        secondary: r.community_display_name ?? r.community_key ?? undefined,
        count: r.transaction_count,
        communityKey: r.community_key ?? undefined,
        communityDisplayName: r.community_display_name ?? undefined,
        zoneKey: r.zone_key ?? undefined,
        zoneDisplayName: r.zone ?? undefined,
      }));
    },
    [communityKeyValue, zoneKeyValue],
  );

  // ADR-055 — hierarchical key clearing: a free-text edit on a level's label
  // invalidates its key AND every child key (zone → community → building).
  // commitOption fires onChange(label) BEFORE onCommitOption(option), so a
  // combobox pick clears here then immediately re-sets the canonical keys.
  const clearKeysFrom = useCallback(
    (level: 'zone' | 'community' | 'building') => {
      if (level === 'zone') form.setValue('zone_key', null, { shouldDirty: true });
      if (level !== 'building') form.setValue('community_key', null, { shouldDirty: true });
      form.setValue('building_key', null, { shouldDirty: true });
    },
    [form],
  );

  // Persist the in-progress form to localStorage so an accidental refresh
  // does not wipe the admin's work. Keyed by user id so two admins on a
  // shared machine cannot see each other's drafts. Disabled until auth is
  // ready to avoid a moment-of-anon write under the wrong key.
  const draft = useFormDraftAutosave({
    form,
    storageKey: userId ? `ddm:deal-draft:${userId}` : 'ddm:deal-draft:anon',
    enabled: auth.status === 'authenticated',
  });

  // Show the "draft restored" toast once per mount.
  const draftToastShownRef = useRef(false);
  useEffect(() => {
    if (!draft.hasRecovered || draftToastShownRef.current) return;
    draftToastShownRef.current = true;
    const minutesAgo = draft.draftAgeMinutes ?? 0;
    toast.info(t('admin.dealEdit.create.draftRestored', { count: minutesAgo, minutesAgo }), {
      duration: 8000,
      action: {
        label: t('admin.dealEdit.create.draftDiscard'),
        onClick: () => {
          draft.clearDraft();
          form.reset(NEW_DEFAULTS);
        },
      },
    });
  }, [draft, t, form]);

  const blocker = useFormDirty({
    isDirty: form.formState.isDirty,
    isPending: submitting,
  });

  // Options for the harmonised Property type <Select>. Memoised so RHF's
  // identity-stable subscribers (the combobox) don't re-render on every keystroke.
  const propertyTypeOptions = useMemo<SelectOption[]>(
    () =>
      PROPERTY_TYPES.map((pt) => ({
        value: pt,
        label: t(`admin.dealEdit.propertyTypeOpts.${pt}`),
      })),
    [t],
  );

  // RHF's onInvalid callback — fires when validation rejects the submission.
  // Toast the user, switch to the first offending tab and try to focus the
  // first offending field (Spec UB14). `setFocus` is a no-op for non-registered
  // inputs like the custom <Select>, so we swallow errors silently.
  const handleInvalid = (errors: FieldErrors<AdminDealCreateValues>) => {
    const count = Object.keys(errors).length;
    toast.error(t('admin.dealEdit.validation.missingFieldsToast', { count }));
    const found = firstErrorLocation(errors);
    if (!found) return;
    setTab(found.tab);
    setTimeout(() => {
      try {
        form.setFocus(found.field);
      } catch {
        // Custom select / segmented control — no DOM input registered.
      }
    }, 50);
  };

  const handleCreate = form.handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      const payload: AdminDealCreatePayload = {
        type: values.type,
        title: values.title,
        description: values.description,
        zone: values.zone,
        community: values.community,
        building: values.building,
        zone_key: values.zone_key,
        community_key: values.community_key,
        building_key: values.building_key,
        latitude: values.latitude,
        longitude: values.longitude,
        property_type: values.property_type,
        area_sqft: values.area_sqft,
        bedrooms: values.bedrooms,
        bathrooms: values.bathrooms,
        floor: values.floor,
        view: values.view,
        handover: values.handover,
        asking_price: values.asking_price,
        original_price: values.original_price,
        amount_paid: values.amount_paid,
        expiration_date: values.expiration_date,
        internal_notes: values.internal_notes,
        advisor_id: values.advisor_id || null,
      };
      const created = await createAdminDeal(payload);
      draft.clearDraft();
      toast.success(t('admin.dealEdit.create.toastSuccess'));
      navigate(`/admin/deals/${created.id}/edit`);
    } catch (err) {
      const pg = getPgError(err);
      const msg = pg.message.toLowerCase();
      if (pg.code === '42501') {
        toast.error(t('admin.dealEdit.create.toastErrorRls'));
      } else if (pg.code === '23514' || msg.includes('check constraint')) {
        toast.error(t('admin.dealEdit.create.toastErrorCheck'));
      } else {
        toast.error(t('admin.dealEdit.create.toastError'));
      }
    } finally {
      setSubmitting(false);
    }
  }, handleInvalid);

  return (
    <>
      <header className="admin-head">
        <div>
          <h1 className="admin-head__title">{t('admin.dealEdit.create.title')}</h1>
          <div className="admin-head__sub">{t('admin.dealEdit.create.modeBadge')}</div>
        </div>
        <div className="admin-head__actions">
          <button
            type="button"
            className="admin-btn admin-btn--outline"
            disabled={submitting}
            onClick={() => navigate('/admin/deals')}
          >
            {t('admin.dealEdit.create.cancelButton')}
          </button>
          <button type="button" className="admin-btn" disabled={submitting} onClick={handleCreate}>
            {t('admin.dealEdit.create.button')}
          </button>
        </div>
      </header>

      <div className="admin-info-banner" role="note">
        <span aria-hidden="true" className="admin-info-banner__icon">
          ⓘ
        </span>
        <span>{t('admin.dealEdit.create.mediaAfterSaveHint')}</span>
      </div>

      <div className="admin-tabs" role="tablist">
        {TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={`admin-tab${tab === key ? ' is-active' : ''}`}
            onClick={() => key !== 'media' && setTab(key)}
            disabled={key === 'media'}
            title={key === 'media' ? t('admin.dealEdit.create.mediaUnavailable') : undefined}
          >
            {t(`admin.dealEdit.tabs.${key}`)}
          </button>
        ))}
      </div>

      {blocker.state === 'blocked' ? (
        <ConfirmModal
          title={t('admin.dealEdit.confirm.discardChanges.title')}
          body={t('admin.dealEdit.confirm.discardChanges.body')}
          confirmLabel={t('admin.dealEdit.confirm.discardChanges.confirmLabel')}
          danger
          onConfirm={() => blocker.proceed?.()}
          onCancel={() => blocker.reset?.()}
        />
      ) : null}

      <form onSubmit={handleCreate} noValidate>
        {tab === 'type' && (
          <div className="admin-form">
            <div style={{ marginBottom: 24 }}>
              <div className="admin-fieldset-label">{t('admin.dealEdit.fields.type')}</div>
              <div className="admin-segmented">
                {(['ready', 'off_plan'] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`admin-segmented__btn${form.watch('type') === opt ? ' is-active' : ''}`}
                    onClick={() => form.setValue('type', opt, { shouldDirty: true })}
                  >
                    {t(
                      opt === 'ready'
                        ? 'admin.dealEdit.fields.ready'
                        : 'admin.dealEdit.fields.offPlan',
                    )}
                  </button>
                ))}
              </div>
              {form.formState.errors.type ? (
                <div className="admin-form__error">{form.formState.errors.type.message}</div>
              ) : null}
            </div>

            <TextField
              label={t('admin.dealEdit.fields.expiry')}
              type="date"
              helper={t('admin.dealEdit.fields.expiryHint')}
              {...form.register('expiration_date')}
            />
            <Textarea
              label={t('admin.dealEdit.fields.internalNotes')}
              rows={3}
              {...form.register('internal_notes')}
            />
            <AdvisorSelectNew
              value={form.watch('advisor_id') ?? null}
              onChange={(id) => form.setValue('advisor_id', id, { shouldDirty: true })}
            />
          </div>
        )}

        {tab === 'property' && (
          <div className="admin-form">
            <TextField
              label={t('admin.dealEdit.fields.title')}
              required
              error={form.formState.errors.title?.message}
              {...form.register('title')}
            />
            <div>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem' }}>
                {t('admin.dealEdit.fields.description')}
              </label>
              <HtmlEditor
                value={form.watch('description') ?? ''}
                onChange={(html) => form.setValue('description', html, { shouldDirty: true })}
                ariaLabel={t('admin.dealEdit.fields.description')}
                style={{ minHeight: '180px' }}
              />
            </div>
            <div className="admin-form__row-grid">
              <Controller
                control={form.control}
                name="zone"
                render={({ field, fieldState }) => (
                  <LocationCombobox
                    name="zone"
                    label={t('admin.dealEdit.fields.zone')}
                    required
                    error={fieldState.error?.message}
                    value={field.value ?? ''}
                    onChange={(v) => {
                      field.onChange(v);
                      clearKeysFrom('zone');
                    }}
                    onSearch={searchZoneOptions}
                    onCommitOption={(opt) => {
                      form.setValue('zone_key', opt.key, { shouldDirty: true });
                    }}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="community"
                render={({ field, fieldState }) => (
                  <LocationCombobox
                    name="community"
                    label={t('admin.dealEdit.fields.community')}
                    required
                    error={fieldState.error?.message}
                    value={field.value ?? ''}
                    onChange={(v) => {
                      field.onChange(v);
                      clearKeysFrom('community');
                    }}
                    onSearch={searchCommunityOptions}
                    onCommitOption={(opt) => {
                      // Persist the canonical key + auto-fill the parent zone
                      // (key AND label) when the picked community carries one.
                      form.setValue('community_key', opt.key, { shouldDirty: true });
                      if (opt.zoneKey) {
                        form.setValue('zone_key', opt.zoneKey, { shouldDirty: true });
                      }
                      if (opt.zoneDisplayName) {
                        form.setValue('zone', opt.zoneDisplayName, {
                          shouldDirty: true,
                          shouldValidate: false,
                        });
                      }
                    }}
                  />
                )}
              />
              <Controller
                control={form.control}
                name="building"
                render={({ field, fieldState }) => (
                  <LocationCombobox
                    name="building"
                    label={t('admin.dealEdit.fields.building')}
                    error={fieldState.error?.message}
                    value={field.value ?? ''}
                    onChange={(v) => {
                      field.onChange(v);
                      clearKeysFrom('building');
                    }}
                    onSearch={searchBuildingOptions}
                    onCommitOption={(opt) => {
                      // Persist the canonical building key + its available
                      // parents (keys AND labels). The combobox only ever
                      // offers dld_buildings rows, so a text matching only a
                      // project GROUP can never assign a specific tower here.
                      // `community_display_name` comes from the LEFT JOIN in
                      // search_dld_buildings (mig 20260522010400).
                      form.setValue('building_key', opt.key, { shouldDirty: true });
                      if (opt.communityKey) {
                        form.setValue('community_key', opt.communityKey, { shouldDirty: true });
                      }
                      if (opt.communityDisplayName) {
                        form.setValue('community', opt.communityDisplayName, {
                          shouldDirty: true,
                          shouldValidate: false,
                        });
                      }
                      if (opt.zoneKey) {
                        form.setValue('zone_key', opt.zoneKey, { shouldDirty: true });
                      }
                      if (opt.zoneDisplayName) {
                        form.setValue('zone', opt.zoneDisplayName, {
                          shouldDirty: true,
                          shouldValidate: false,
                        });
                      }
                    }}
                  />
                )}
              />
              <div>
                <label
                  htmlFor="maps-link-new"
                  style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem' }}
                >
                  {t('admin.dealEdit.fields.mapsLink')}
                </label>
                <input
                  id="maps-link-new"
                  type="text"
                  className="m5-field__input"
                  placeholder={t('admin.dealEdit.fields.mapsLinkPlaceholder')}
                  onChange={(e) => {
                    const text = e.target.value;
                    const atMatch = text.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
                    const qMatch = text.match(/[?&](?:q|query)=(-?\d+\.\d+)[,+](-?\d+\.\d+)/);
                    const rawMatch = text.match(/^(-?\d+\.\d{3,}),\s*(-?\d+\.\d{3,})$/);
                    const match = atMatch ?? qMatch ?? rawMatch;
                    if (match) {
                      form.setValue('latitude', Number(match[1]), { shouldDirty: true });
                      form.setValue('longitude', Number(match[2]), { shouldDirty: true });
                    }
                  }}
                />
                <div style={{ marginTop: '0.25rem', fontSize: '0.75rem' }}>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      [form.watch('building'), form.watch('community'), 'Dubai']
                        .filter((s): s is string => !!s && s.trim().length > 0)
                        .join(', ') || 'Dubai',
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--c-link, #2563eb)' }}
                  >
                    {t('admin.dealEdit.fields.findOnMaps')}
                  </a>
                </div>
              </div>
              <input
                type="hidden"
                {...form.register('latitude', {
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              <input
                type="hidden"
                {...form.register('longitude', {
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              <div>
                <div className="admin-fieldset-label" id="admin-deal-new-pt-label">
                  {t('admin.dealEdit.fields.propertyType')}
                </div>
                <Select
                  ariaLabel={t('admin.dealEdit.fields.propertyType')}
                  options={propertyTypeOptions}
                  value={form.watch('property_type')}
                  onChange={(v) =>
                    form.setValue('property_type', (v ?? 'apartment') as PropertyType, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  className="admin-select-block"
                />
                {form.formState.errors.property_type ? (
                  <div className="admin-form__error">
                    {form.formState.errors.property_type.message}
                  </div>
                ) : null}
              </div>
              <TextField
                label={t('admin.dealEdit.fields.bedrooms')}
                type="number"
                error={form.formState.errors.bedrooms?.message}
                {...form.register('bedrooms', { valueAsNumber: true })}
              />
              <TextField
                label={t('admin.dealEdit.fields.bathrooms')}
                type="number"
                error={form.formState.errors.bathrooms?.message}
                {...form.register('bathrooms', {
                  valueAsNumber: true,
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              <TextField
                label={t('admin.dealEdit.fields.area')}
                type="number"
                error={form.formState.errors.area_sqft?.message}
                {...form.register('area_sqft', { valueAsNumber: true })}
              />
              <TextField
                label={t('admin.dealEdit.fields.floor')}
                type="number"
                {...form.register('floor', {
                  valueAsNumber: true,
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              <TextField label={t('admin.dealEdit.fields.view')} {...form.register('view')} />
              <TextField
                label={t('admin.dealEdit.fields.handover')}
                {...form.register('handover')}
              />
            </div>
          </div>
        )}

        {tab === 'price' && (
          <div className="admin-form">
            <div className="admin-form__row-grid" style={{ marginBottom: 24 }}>
              <TextField
                label={t('admin.dealEdit.fields.askingPrice')}
                type="number"
                required
                helper={t('admin.dealEdit.fields.askingPriceMinHint')}
                error={form.formState.errors.asking_price?.message}
                {...form.register('asking_price', { valueAsNumber: true })}
              />
              <TextField
                label={t('admin.dealEdit.fields.originalPrice')}
                type="number"
                {...form.register('original_price', {
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
              <TextField
                label={t('admin.dealEdit.fields.amountPaid')}
                type="number"
                {...form.register('amount_paid', {
                  setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                })}
              />
            </div>
          </div>
        )}
      </form>
    </>
  );
}
